import { Elements } from '@stripe/react-stripe-js'
import {
  loadStripe,
  Stripe,
  StripeElementLocale,
  StripeElementsOptions,
} from '@stripe/stripe-js'
import { wrapFontFamily } from 'common/components/entities/Text/utils'
import { zeroDecimalCurrencies } from 'common/constants/stripe-consts'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { PricePlanTypeEnum } from 'common/enums/PricePlanTypeEnum'
import { CurrencyEnum } from 'common/types/CurrencyType'
import { isStripePaymentMethod } from 'publisher/components/entities/PaymentMethod/utils/paymentCreditCardUtils'
import pricePlanTypes from 'publisher/constants/pricePlanTypes'
import { useIsHundredPercentDiscount } from 'publisher/hooks/payment/use-hundred-percent-discount'
import { useFinalAmount } from 'publisher/pages/offer-page/hooks/use-final-amount'
import { usePage, usePayment } from 'publisher/store'
import pageSelectors from 'publisher/store/page/pageSelectors'
import {
  getActivePaymentMethod,
  getActiveStripePaymentMethod,
  getCheckedPricePlan,
  getOfferCurrency,
  getOfferPricing,
  getStripeAccountId,
} from 'publisher/store/payment/paymentSelectors'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useCalculatedPricePlans } from 'src/publisher/hooks/use-calculated-price-plans'
import { centsToEuro } from 'src/shared/utils/price-calculator'
import Meta from '../components/core/Meta'
import { DLocalProvider } from '../context/DlocalContext'
import { MercadoPagoProvider } from '../context/mercadoPagoContext'
import { XenditProvider } from '../context/XenditContext'
import usePhysicalProductsFromQueryString from '../hooks/usePhysicalProductsFromQueryString'
import { OnExitPopup } from './components/on-exit-popup'
import { OnloadPopups } from './components/onload-popups'
import { TriggeredPopup } from './components/triggered-popup'
import PageUi from './ui/PageUi'

const PaymentPage = ({ children }: PropsWithChildren) => {
  const globalTextFontProperties = usePage(
    pageSelectors.getGlobalFontProperties,
  )
  const pageLocale = usePage(pageSelectors.getLocale)
  const stripeAccount = usePayment(getStripeAccountId)
  const activePaymentMethod = usePayment(getActivePaymentMethod)
  // const paymentMethods = usePayment(paymentSelectors.getPaymentMethods)

  const isStripeSelected = activePaymentMethod
    ? isStripePaymentMethod(activePaymentMethod)
    : false
  const stripePaymentMethod = usePayment(getActiveStripePaymentMethod)
  const { pricePlans, activePlanId } = useCalculatedPricePlans()
  usePhysicalProductsFromQueryString()
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: no need for exhaustive deps (except for locale?)
  useEffect(() => {
    if (
      isStripeSelected &&
      stripeAccount &&
      !stripePromise &&
      process.env.STRIPE_KEY
    ) {
      setStripePromise(
        loadStripe(process.env.STRIPE_KEY, {
          stripeAccount,
          locale: pageLocale as StripeElementLocale,
        }),
      )
    }
  }, [isStripeSelected, stripeAccount])

  const offerCurrency = usePayment(getOfferCurrency)
  const finalAmount = useFinalAmount()
  const checkedPricePlan = usePayment(getCheckedPricePlan)

  const offerPricings = usePayment(getOfferPricing)
  const isHundredPercentDiscount = useIsHundredPercentDiscount()

  //https://app.shortcut.com/systeme/story/73336/revert-stripe
  // const stripeOptions: StripeElementsOptions = useMemo(() => {
  //   const currentPlan = pricePlans.find(plan => plan.id === activePlanId)

  //   const isOneShot =
  //     currentPlan?.type !== PricePlanTypeEnum.LimitedSubscription &&
  //     currentPlan?.type !== PricePlanTypeEnum.Subscription
  //   if (
  //     finalAmount &&
  //     isOneShot &&
  //     !isHundredPercentDiscount &&
  //     PaymentMethodEnum.StripeIDeal !== stripePaymentMethod &&
  //     PaymentMethodEnum.StripeBancontact !== stripePaymentMethod &&
  //     PaymentMethodEnum.StripeKlarna !== stripePaymentMethod
  //   ) {
  //     return {
  //       mode: 'payment',
  //       // https://docs.stripe.com/currencies#minor-units
  //       amount: zeroDecimalCurrencies.includes(offerCurrency as CurrencyEnum)
  //         ? centsToEuro(finalAmount)
  //         : finalAmount,
  //       currency: offerCurrency,
  //       paymentMethodCreation: 'manual',
  //     }
  //   }
  //   return {
  //     mode: 'setup',
  //     currency: offerCurrency,
  //     paymentMethodCreation: 'manual',
  //   }
  // }, [
  //   finalAmount,
  //   offerCurrency,
  //   activePlanId,
  //   pricePlans,
  //   stripePaymentMethod,
  //   coupon,
  //   selectedOfferPricing,
  //   offerPricings,
  // ])

  const stripeOptions: StripeElementsOptions = useMemo(() => {
    const currentPlan = pricePlans.find(plan => plan.id === activePlanId)

    const isOneShotWithoutIDealOrBancontact =
      currentPlan?.type === pricePlanTypes.oneShot &&
      activePaymentMethod !== PaymentMethodEnum.StripeIDeal &&
      activePaymentMethod !== PaymentMethodEnum.StripeBancontact

    const isMultibanco =
      activePaymentMethod === PaymentMethodEnum.StripeMultibanco

    const isKlarnaSubscriptionWithoutTrial =
      activePaymentMethod === PaymentMethodEnum.StripeKlarna &&
      checkedPricePlan?.type !== PricePlanTypeEnum.OneShot &&
      !checkedPricePlan?.subscriptionPlan.trialPeriod

    const paymentMethodTypes: string[] | undefined = (() => {
      if (!activePaymentMethod) {
        return []
      }

      const isStripe = activePaymentMethod.startsWith('stripe:')
      const isApplePay =
        activePaymentMethod === PaymentMethodEnum.StripeApplepay
      const isMultibanco =
        activePaymentMethod === PaymentMethodEnum.StripeMultibanco
      const isFree = finalAmount === 0

      if (!isStripe || isApplePay) {
        return []
      }
      if (isMultibanco && isFree) {
        return []
      }

      return [activePaymentMethod.split(':')[1]]
    })()
    if (
      finalAmount &&
      (((isOneShotWithoutIDealOrBancontact ||
        isKlarnaSubscriptionWithoutTrial) &&
        !isHundredPercentDiscount) ||
        isMultibanco)
    ) {
      return {
        mode: 'payment',
        // https://docs.stripe.com/currencies#minor-units
        amount: zeroDecimalCurrencies.includes(offerCurrency as CurrencyEnum)
          ? centsToEuro(finalAmount)
          : finalAmount,
        currency: offerCurrency,
        paymentMethodCreation: 'manual',
        setupFutureUsage: isMultibanco ? null : 'off_session',
        paymentMethodTypes,
      }
    }
    return {
      mode: 'setup',
      currency: offerCurrency,
      paymentMethodCreation: 'manual',
      setupFutureUsage: isMultibanco ? null : 'off_session',
      paymentMethodTypes,
    }
  }, [
    finalAmount,
    offerCurrency,
    activePlanId,
    pricePlans,
    stripePaymentMethod,
    offerPricings,
    activePaymentMethod,
    checkedPricePlan,
    isHundredPercentDiscount,
  ])

  return (
    <Elements
      stripe={stripePromise}
      options={{
        ...stripeOptions,
        appearance: {
          rules: {
            '.Input': {
              boxShadow: 'none',
            },
            '.AccordionItem': {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
            '.Block': {
              backgroundColor: 'transparent',
            },
          },
        },
      }}
    >
      {/* @ts-expect-error worked before ts */}
      <DLocalProvider>
        <form id="form-checkout">
          {/* @ts-expect-error worked before ts */}
          <MercadoPagoProvider>
            {/* @ts-expect-error worked before ts */}
            <XenditProvider>
              <PageUi
                $textFontFamily={wrapFontFamily(
                  globalTextFontProperties.fontFamily,
                )}
                $textFontWeight={globalTextFontProperties.fontWeight}
                $textFontStyle={globalTextFontProperties.fontStyle}
              >
                <Meta />
                {children}
                <TriggeredPopup />
                <OnExitPopup />
                <OnloadPopups />
              </PageUi>
            </XenditProvider>
          </MercadoPagoProvider>
        </form>
      </DLocalProvider>
    </Elements>
  )
}

export default PaymentPage
